import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { CompanyService, PartnerService } from 'us-web-services';
import { Table, TableInstance } from 'src/components/common/Table';
import PageStyles from 'src/styles/PageStyles';
import useCompany from '../../../store/useCompany';

const FILTER_STORAGE_KEY = 'companiesFilters';

function CompanyLookup(props: RouteComponentProps) {
  const companyActions = useCompany()[1];
  const { history } = props;
  const { classes } = PageStyles();

  const tableRef = React.useRef<TableInstance>();

  const getInitialFilters = () => {
    if (typeof window === 'undefined') {
      return {
        partnerIds: [] as number[],
        hasByop: false,
        hasOon: false,
        statusIds: [] as number[],
      };
    }

    const stored = window.sessionStorage.getItem(FILTER_STORAGE_KEY);

    if (!stored) {
      return {
        partnerIds: [] as number[],
        hasByop: false,
        hasOon: false,
        statusIds: [] as number[],
      };
    }

    try {
      const parsed = JSON.parse(stored) as {
        partnerIds?: number[];
        hasByop?: boolean;
        hasOon?: boolean;
        statusIds?: number[];
      };

      return {
        partnerIds: parsed.partnerIds || [],
        hasByop: typeof parsed.hasByop === 'boolean' ? parsed.hasByop : false,
        hasOon: typeof parsed.hasOon === 'boolean' ? parsed.hasOon : false,
        statusIds: parsed.statusIds || [],
      };
    } catch {
      return {
        partnerIds: [] as number[],
        hasByop: false,
        hasOon: false,
        statusIds: [] as number[],
      };
    }
  };

  const initialFilters = getInitialFilters();

  const [partners, setPartners] = useState([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<number[]>(
    initialFilters.partnerIds,
  );
  const [hasByop, setHasByop] = useState(initialFilters.hasByop);
  const [hasOon, setHasOon] = useState(initialFilters.hasOon);
  const [selectedStatusIds, setSelectedStatusIds] = useState<number[]>(
    initialFilters.statusIds,
  );

  useEffect(() => {
    const getPartners = async () => {
      const result = (await PartnerService.getByFilter()).data;

      setPartners(result.data);
    };

    getPartners();
  }, []);

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.fetchData();
    }
  }, [selectedPartnerIds, hasByop, hasOon, selectedStatusIds]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const value = JSON.stringify({
      partnerIds: selectedPartnerIds,
      hasByop,
      hasOon,
      statusIds: selectedStatusIds,
    });

    window.sessionStorage.setItem(FILTER_STORAGE_KEY, value);
  }, [selectedPartnerIds, hasByop, hasOon, selectedStatusIds]);

  const COLUMNS = useMemo(
    () => [
      {
        id: 'companies',
        header: 'Companies',
        columns: [
          {
            header: 'ID',
            size: 20,
            accessorKey: 'id',
          },
          {
            header: 'Name',
            accessorKey: 'name',
          },
          {
            header: 'Display Name',
            accessorKey: 'displayName',
          },
          {
            header: 'Alias',
            accessorKey: 'alias',
          },
          {
            header: 'External ID?',
            accessorFn: row => (row.useExternalId ? 'Yes' : 'No'),
            size: 20,
          },
          {
            header: 'Email validation?',
            accessorFn: row => (row.emailValidationEnabled ? 'Yes' : 'No'),
            size: 20,
          },
          {
            header: 'Zendesk enabled?',
            accessorFn: row => (row.zendeskEnabled ? 'Yes' : 'No'),
            size: 20,
          },
          {
            header: 'Logo',
            accessorFn: row => row.logoUrl,
            Cell: ({ cell }) => (
              <img alt='' src={cell.getValue()} style={{ width: 50 }} />
            ),
            size: 60,
          },
        ],
      },
    ],
    [],
  );

  const editCompany = rowData => {
    companyActions.set(rowData);
    history.push(`/companies/${rowData.id}`);
  };

  const addCompany = () => {
    companyActions.set({});
    history.push('/company-create');
  };

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.fetchData();
    }
  }, [selectedPartnerIds, hasByop, hasOon, selectedStatusIds]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const value = JSON.stringify({
      partnerIds: selectedPartnerIds,
      hasByop,
      hasOon,
      statusIds: selectedStatusIds,
    });

    window.sessionStorage.setItem(FILTER_STORAGE_KEY, value);
  }, [selectedPartnerIds, hasByop, hasOon, selectedStatusIds]);

  const handleNumericSearch = (searchTerm: string): boolean => {
    if (/^\d+$/.test(searchTerm.trim())) {
      history.push(`/companies/${searchTerm.trim()}`);

      return true;
    }

    return false;
  };

  const params = (globalFilter: string) => {
    const paramArray = [];

    if (globalFilter) {
      if (handleNumericSearch(globalFilter)) {
        return paramArray;
      }
      paramArray.push({
        key: 'q',
        value: `name.startsWith('${globalFilter}')`,
      });
    }

    if (selectedPartnerIds.length > 0) {
      selectedPartnerIds.forEach(id => {
        paramArray.push({
          key: 'partnerIds',
          value: id.toString(),
        });
      });
    }

    if (hasByop) {
      paramArray.push({
        key: 'hasByop',
        value: 'true',
      });
    }

    if (hasOon) {
      paramArray.push({
        key: 'hasOon',
        value: 'true',
      });
    }

    if (selectedStatusIds.length > 0) {
      selectedStatusIds.forEach(statusId => {
        paramArray.push({
          key: 'benefitStatusIds',
          value: statusId.toString(),
        });
      });
    }

    return paramArray;
  };

  const getSelectedPartnerLabel = (selectedIds: number[]) => {
    const selectedPartners = partners.filter(partner =>
      selectedIds.indexOf(partner.id) > -1,
    );

    if (selectedPartners.length === 0) {
      return '';
    }

    const names = selectedPartners.map(partner => partner.name);

    if (names.length <= 2) {
      return names.join(', ');
    }

    const [first, second, ...rest] = names;

    return `${first}, ${second} + ${rest.length} more`;
  };

  return (
    <Paper className={classes.paper}>
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <FormControl size='small' style={{ minWidth: 200 }}>
          <InputLabel id='partner-filter-label'>Partner</InputLabel>
          <Select
            labelId='partner-filter-label'
            multiple
            size='small'
            value={selectedPartnerIds}
            onChange={event => {
              const value = event.target.value as number[];

              setSelectedPartnerIds(value);
            }}
            renderValue={selected => getSelectedPartnerLabel(selected as number[])}
          >
            {partners.map(partner => (
              <MenuItem key={partner.id} value={partner.id}>
                <Checkbox checked={selectedPartnerIds.indexOf(partner.id) > -1} />
                <ListItemText primary={partner.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' style={{ minWidth: 120 }}>
          <InputLabel id='status-filter-label'>Status</InputLabel>
          <Select
            labelId='status-filter-label'
            multiple
            size='small'
            value={selectedStatusIds}
            onChange={event => {
              const value = event.target.value as number[];

              setSelectedStatusIds(value);
            }}
            renderValue={selected =>
              selected
                .map(statusId => (statusId === 2 ? 'Active' : 'Pending'))
                .join(', ')
            }
          >
            <MenuItem value={1}>
              <Checkbox checked={selectedStatusIds.indexOf(1) > -1} />
              <ListItemText primary='Pending' />
            </MenuItem>
            <MenuItem value={2}>
              <Checkbox checked={selectedStatusIds.indexOf(2) > -1} />
              <ListItemText primary='Active' />
            </MenuItem>
          </Select>
        </FormControl>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={hasByop}
                onChange={event => setHasByop(event.target.checked)}
              />
            )}
            label='BYOP'
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={hasOon}
                onChange={event => setHasOon(event.target.checked)}
              />
            )}
            label='OON'
          />
        </div>

        <Button
          size='small'
          variant='outlined'
          onClick={() => {
            setSelectedPartnerIds([]);
            setHasByop(false);
            setHasOon(false);
            setSelectedStatusIds([]);
          }}
        >
          Clear filters
        </Button>
      </div>
      <Table
        ref={tableRef}
        columns={COLUMNS}
        retrieveData={CompanyService.getByFilter}
        params={params}
        alwaysApplyParams
        editAction={editCompany}
        addAction={addCompany}
      />
    </Paper>
  );
}

export default withRouter(CompanyLookup);
